import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Mail, Phone, Clock, UserCog, Scissors, Users, BellRing } from "lucide-react";
import { norm } from "@/lib/normalize";
import { useLookups } from "@/context/LookupContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { formatINR } from "@/lib/currency";
import { formatDateTime } from "@/lib/datetime";
import { formatDate } from "@/lib/datetime";


const emptyStaff = { firstName: "", lastName: "", email: "", designation: "", phone: "", bio: "", roleId: "", hireDate: "", gender: "", bookable: true };

const DAYS = [
  { key: "MONDAY", label: "MONDAY" },
  { key: "TUESDAY", label: "TUESDAY" },
  { key: "WEDNESDAY", label: "WEDNESDAY" },
  { key: "THURSDAY", label: "THURSDAY" },
  { key: "FRIDAY", label: "FRIDAY" },
  { key: "SATURDAY", label: "Saturday" },
  { key: "SUNDAY", label: "SUNDAY" },
];

const DEFAULT_HOURS = DAYS.reduce((a, d) => ({ ...a, [d.key]: { startTime: "09:00", endTime: "21:00", working: d.key !== "SUNDAY" } }), {});

function HoursEditor({ staffId, isAdmin }) {
  const { lookups } = useLookups();

  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/staff/${staffId}/hours`)
      .then((r) => {
        if (!alive) return;
        const raw = r.data || [];
        // Handle both array response and object with schedule/leaveDates
        const schedule = Array.isArray(raw) ? raw : (Array.isArray(raw.schedule) ? raw.schedule : raw.hours || []);
        const mapped = Array.isArray(schedule)
          ? schedule.reduce((acc, item) => ({
              ...acc,
              [item.dayOfWeek]: {
                id: item.id,
                staffId: item.staffId,
                startTime: item.startTime ? item.startTime.slice(0, 5) : "09:00",
                endTime: item.endTime ? item.endTime.slice(0, 5) : "21:00",
                working: item.working ?? item.isWorking ?? true,
              }
            }), {})
          : {};
        setHours({ ...DEFAULT_HOURS, ...mapped });
      })
      .catch(() => toast.error("Failed to load hours"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [staffId]);

  
  const setDay = (key, patch) => setHours((h) => ({ ...h, [key]: { ...h[key], ...patch } }));
  

  const save = async () => {
    setSaving(true);
    try {
      // Transform object back into array for the backend
      const payload = DAYS.map((d) => {
        const h = hours[d.key] || {};
        return {
          ...(h.id ? { id: h.id } : {}),
          staffId: staffId,
          dayOfWeek: d.key,
          startTime: h.startTime || null,
          endTime: h.endTime || null,
          working: h.working ?? false,
        };
      });
      // Use POST for new schedule (no ids), PUT for updating existing
      const hasExisting = payload.some((item) => item.id);
      if (hasExisting) {
        await api.put(`/staff/${staffId}/hours`, payload);
      } else {
        await api.post(`/staff/${staffId}/hours`, payload);
      }
      toast.success("Working hours saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-emerald-muted py-4">Loading hours…</div>;

  return (
    <div className="space-y-3" data-testid={`hours-editor-${staffId}`}>
      {DAYS.map((d) => {
        const h = hours[d.key] || { startTime: "09:00", endTime: "21:00", working: false };
        return (
          <div key={d.key} className="grid grid-cols-12 gap-3 items-center bg-emerald-elev/40 border border-gold-subtle rounded-md px-3 py-2"
            data-testid={`hours-row-${staffId}-${d.key}`}>
            <div className="col-span-3 text-white text-sm">{d.label}</div>
            <div className="col-span-3">
              <Input type="time" value={h.startTime} disabled={!isAdmin || !h.working}
                onChange={(e) => setDay(d.key, { startTime: e.target.value })}
                className="bg-emerald-elev border-gold-subtle text-white h-9"
                data-testid={`hours-${staffId}-${d.key}-start`} />
            </div>
            <div className="col-span-1 text-center text-emerald-muted text-xs">to</div>
            <div className="col-span-3">
              <Input type="time" value={h.endTime} disabled={!isAdmin || !h.working}
                onChange={(e) => setDay(d.key, { endTime: e.target.value })}
                className="bg-emerald-elev border-gold-subtle text-white h-9"
                data-testid={`hours-${staffId}-${d.key}-end`} />
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <Switch checked={h.working} disabled={!isAdmin}
                onCheckedChange={(v) => setDay(d.key, { working: !!v })}
                data-testid={`hours-${staffId}-${d.key}-closed`} />
              <span className="text-xs text-emerald-muted">{h.working ? "On" : "Off"}</span>
            </div>
          </div>
        );
      })}
      {isAdmin && (
        <button onClick={save} disabled={saving}
          className="gold-button px-5 py-2 rounded-md mt-2 disabled:opacity-60"
          data-testid={`save-hours-${staffId}`}>
          {saving ? "Saving…" : "Save Working Hours"}
        </button>
      )}
    </div>
  );
}

function StaffServicesGrid({ staffId, month, dateFrom, dateTo }) {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    api.get(`/staff/${staffId}/services-performed?month=${month}`)
      .then((r) => { if (alive) setAllRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) { setError(true); setAllRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [staffId, month]);

  // Filter the month's rows down to the selected date range (inclusive).
  const rows = allRows.filter((r) => {
    if (!dateFrom && !dateTo) return true;
    if (!r.invoiceDate) return false;
    const day = String(r.invoiceDate).slice(0, 10); // yyyy-MM-dd
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  });

  const total = rows.reduce((s, r) => s + Number(r.lineTotal || 0), 0);
  const serviceCount = rows.reduce((s, r) => s + Number(r.quantity || 1), 0);

  if (loading) return <div className="text-sm text-emerald-muted py-4 text-center">Loading services…</div>;
  if (error) return <div className="text-sm text-red-300 py-4 text-center">Failed to load services.</div>;
  if (rows.length === 0) return <div className="text-sm text-emerald-muted py-8 text-center">No services recorded for this selection.</div>;

  return (
    <div className="border border-gold-subtle rounded-lg overflow-hidden" data-testid={`staff-services-grid-${staffId}`}>
      <Table>
        <TableHeader>
          <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
            <TableHead className="th-gold">Customer</TableHead>
            <TableHead className="th-gold">Service</TableHead>
            <TableHead className="th-gold">Date</TableHead>
            <TableHead className="th-gold text-right">Unit Price</TableHead>
            <TableHead className="th-gold text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r.invoiceId}-${r.serviceId}-${i}`} className="border-gold-subtle" data-testid={`staff-service-row-${i}`}>
              <TableCell className="text-white">{r.customer || "—"}</TableCell>
              <TableCell className="text-white">{r.serviceName || "—"}</TableCell>
              <TableCell className="text-emerald-muted">{formatDateTime(r.invoiceDate)}</TableCell>
              <TableCell className="text-right text-emerald-muted">{formatINR(r.unitPrice || 0)}</TableCell>
              <TableCell className="text-right text-gold">{formatINR(r.lineTotal || 0)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="border-gold-subtle bg-emerald-elev/40 hover:bg-emerald-elev/40">
            <TableCell colSpan={3} className="text-xs font-bold uppercase tracking-widest text-emerald-muted">
              Total Services: <span className="text-lg font-bold text-gold">{serviceCount}</span>
            </TableCell>
            <TableCell className="text-right text-xs font-bold uppercase tracking-widest text-emerald-muted">Total</TableCell>
            <TableCell className="text-right text-lg font-bold text-gold tabular-nums whitespace-nowrap">{formatINR(total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

const customerIdOf = (c) => c?.customerId ?? c?.id ?? c?.customer?.id;

// Format a Date to a local "YYYY-MM-DDTHH:mm:ss" string (no timezone suffix).
const toLocalDateTime = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

function StaffRemindersGrid({ staffId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Track which customers have already been marked answered this session.
  const [answered, setAnswered] = useState({});
  // Dialog state for capturing notes when marking answered.
  const [reminderDialog, setReminderDialog] = useState(null); // { customer }
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    setAnswered({});
    api.get(`/staff/${staffId}/inactive-customers`)
      .then((r) => { if (alive) setData(r.data || null); })
      .catch(() => { if (alive) { setError(true); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [staffId]);

  const openReminderDialog = (customer) => {
    setNotes("");
    setReminderDialog({ customer });
  };

  const submitReminder = async () => {
    const customer = reminderDialog?.customer;
    const customerId = customerIdOf(customer);
    if (!customerId) { toast.error("Missing customer id for this reminder"); return; }
    setSubmitting(true);
    try {
      await api.post("/staff-customer-reminders", {
        staffId: Number(staffId),
        customerId: Number(customerId),
        isAnswered: true,
        calledAt: toLocalDateTime(new Date()),
        notes: notes,
      });
      toast.success("Reminder recorded");
      setAnswered((prev) => ({ ...prev, [customerId]: true }));
      setReminderDialog(null);
      setNotes("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record reminder");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="text-sm text-emerald-muted py-4 text-center">Loading reminders…</div>;
  if (error) return <div className="text-sm text-red-300 py-4 text-center">Failed to load reminders.</div>;

  const customers = [...(data?.customers || [])].sort((a, b) => {
    // Highest "Days Since" first (descending); missing values sink to the bottom.
    const da = a.daysSinceLastService ?? -Infinity;
    const db = b.daysSinceLastService ?? -Infinity;
    return db - da;
  });

  return (
    <div className="space-y-3" data-testid={`staff-reminders-grid-${staffId}`}>
      <div className="text-lg font-bold text-white">
        There are <span className="text-red-500 underline">{data?.count ?? customers.length}</span> customers to send reminders.
        {data?.cutoffDate && (
          <span className="block text-xs font-normal text-white mt-1">Since {formatDate(data.cutoffDate)}</span>
        )}
      </div>
      {customers.length === 0 ? (
        <div className="text-sm text-emerald-muted py-8 text-center">No inactive customers to remind.</div>
      ) : (
        <div className="border border-gold-subtle rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
                <TableHead className="th-gold">Customer</TableHead>
                <TableHead className="th-gold">Phone</TableHead>
                <TableHead className="th-gold">Last Visited</TableHead>
                <TableHead className="th-gold text-right">Days Since</TableHead>
                <TableHead className="th-gold text-center">Answered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c, i) => {
                const cid = customerIdOf(c);
                const isAnswered = c.isAnswered || answered[cid];
                return (
                  <TableRow key={cid ?? i} className="border-gold-subtle" data-testid={`staff-reminder-row-${i}`}>
                    <TableCell className="text-white">{c.name || "—"}</TableCell>
                    <TableCell className="text-emerald-muted">{c.phone || "—"}</TableCell>
                    <TableCell className="text-emerald-muted">{c.lastServiceDate ? formatDate(c.lastServiceDate) : "Never"}</TableCell>
                    <TableCell className="text-right text-emerald-muted">{c.daysSinceLastService ?? "—"}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={!!isAnswered}
                          disabled={!!isAnswered}
                          onCheckedChange={(v) => { if (v && !isAnswered) openReminderDialog(c); }}
                          className="data-[state=checked]:bg-green-500 disabled:opacity-100"
                          data-testid={`reminder-answered-${i}`}
                        />
                        <span className="text-xs text-emerald-muted">{isAnswered ? "Answered" : "Pending"}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!reminderDialog} onOpenChange={(o) => { if (!o) { setReminderDialog(null); setNotes(""); } }}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md" data-testid="reminder-notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Mark Reminder Answered</DialogTitle>
            <DialogDescription className="text-emerald-muted">
              {reminderDialog?.customer?.name ? `Log the call outcome for ${reminderDialog.customer.name}.` : "Log the call outcome."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-emerald-elev border-gold-subtle mt-1"
                placeholder="e.g. Reminded about the offer, will visit next week"
                data-testid="reminder-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setReminderDialog(null); setNotes(""); }}
                className="px-4 h-10 rounded-md border border-gold-subtle text-emerald-muted hover:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReminder}
                disabled={submitting}
                className="gold-button px-5 h-10 rounded-md disabled:opacity-60"
                data-testid="reminder-submit-btn"
              >
                {submitting ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Staff() {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const staffRoleId = (lookups?.roles || []).find((r) => (r.name || "").toLowerCase() === "staff")?.id || "";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyStaff, roleId: staffRoleId });
  const [editId, setEditId] = useState(null);
  const [activeHoursStaff, setActiveHoursStaff] = useState("");
  const [activeServicesStaff, setActiveServicesStaff] = useState("");
  const [activeRemindersStaff, setActiveRemindersStaff] = useState("");
  const [servicesMonth, setServicesMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [servicesFrom, setServicesFrom] = useState("");
  const [servicesTo, setServicesTo] = useState("");
  const isAdmin = norm(user.roleName) === "admin";
  const canEdit = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist" || norm(user.roleName) === "manager";

  const load = async () => {
    try {
      const r = await api.get("/staff");
      setRows(r.data);
      if (r.data.length && !activeHoursStaff) setActiveHoursStaff(r.data[0].id);
      const firstBookable = r.data.find((s) => s.bookable);
      if (firstBookable && !activeServicesStaff) setActiveServicesStaff(firstBookable.id);
      if (firstBookable && !activeRemindersStaff) setActiveRemindersStaff(firstBookable.id);
    } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/staff/${editId}`, form);
      else await api.post("/staff", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const confirm = useConfirm();
  const del = async (id) => {
    if (!(await confirm({ description: "Remove this stylist?", confirmText: "Remove" }))) return;
    try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="staff-page">
      <div className="flex items-end justify-between gap-4">
        <div><div className="overline mb-2">Team</div><h1 className="font-heading text-4xl text-white inline-flex items-center gap-3"><UserCog className="w-8 h-8 text-purple-500" /> Stylists & Therapists</h1></div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm({ ...emptyStaff, roleId: staffRoleId }); setEditId(null); }} data-testid="add-staff-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Staff</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} staff</DialogTitle>
                <DialogDescription className="text-emerald-muted">Enter stylist details.</DialogDescription>
              </DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First Name</Label><Input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" data-testid="staff-name-input" /></div>
                  <div><Label>Last Name</Label><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" data-testid="staff-lastname-input" /></div>
                </div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Specialty</Label><Input required value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>Hire Date</Label><Input type="date" value={form.hireDate} onChange={(e) => setForm({ ...form, hireDate: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1 text-white" /></div>
                <div>
                  <Label>Gender</Label>
                  <Select value={form.gender} onValueChange={(val) => setForm({ ...form, gender: val })}>
                    <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MALE">Male</SelectItem>
                      <SelectItem value="FEMALE">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox id="bookable" checked={form.bookable} onCheckedChange={(v) => setForm({ ...form, bookable: !!v })} />
                  <Label htmlFor="bookable" className="text-sm cursor-pointer">Bookable (available for appointments)</Label>
                </div>
                <button type="submit" className="gold-button w-full h-11 rounded-md">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="team" className="w-full">
        <TabsList className="bg-emerald-surface border border-gold-subtle" data-testid="staff-tabs">
          <TabsTrigger value="team" data-testid="tab-team"><Users className="w-3 h-3 mr-1 text-gold" /> Stylists</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours"><Clock className="w-3 h-3 mr-1 text-gold" /> Working Hours</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services"><Scissors className="w-3 h-3 mr-1 text-gold" /> Services</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders"><BellRing className="w-3 h-3 mr-1 text-gold" /> Reminders</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rows.map((s) => (
              <div key={s.id} className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6" data-testid={`staff-card-${s.id}`}>
                <div className="w-14 h-14 rounded-full bg-gold text-emerald-950 flex items-center justify-center font-heading text-2xl">
                  {s.firstName.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                </div>
                <h3 className="font-heading text-2xl text-white mt-4">{s.firstName}</h3>
                <div className="overline mt-1">{s.designation}{s.gender ? ` (${s.gender})` : ""}</div>
                <p className="text-sm text-emerald-muted mt-3">{s.bio}</p>
                <div className="mt-4 space-y-1 text-xs text-emerald-muted">
                  <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {s.email}</div>
                  {s.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {s.phone}</div>}
                </div>
                {canEdit && (
                  <div className="mt-4 pt-4 border-t border-gold-subtle flex gap-2">
                    <button onClick={() => { const parts = (s.firstName || "").split(" "); setForm({ firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || s.lastName || "", email: s.email, designation: s.designation, phone: s.phone || "", bio: s.bio || "", roleId: s.roleId || emptyStaff.roleId, hireDate: s.hireDate || "", gender: s.gender || "", bookable: s.bookable ?? true }); setEditId(s.id); setOpen(true); }} className="text-xs text-emerald-muted hover:text-gold inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                    <button onClick={() => del(s.id)} className="text-xs text-emerald-muted hover:text-red-300 inline-flex items-center gap-1 ml-auto" data-testid={`delete-staff-${s.id}`}><Trash2 className="w-3 h-3" /> Remove</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="hours" className="mt-6">
          {rows.length === 0 && <div className="text-emerald-muted">No staff yet.</div>}
          {rows.length > 0 && (
            <div className="grid lg:grid-cols-[220px_1fr] gap-6">
              <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-2 h-fit">
                {rows.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveHoursStaff(s.id)}
                    data-testid={`hours-staff-tab-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                      activeHoursStaff === s.id
                        ? "bg-emerald-elev border border-gold text-white font-medium"
                        : "text-emerald-muted hover:text-white hover:bg-emerald-elev/60"
                    }`}
                  >
                    {s.firstName}
                  </button>
                ))}
              </div>
              <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
                <div className="overline mb-4">Weekly Schedule · {rows.find((s) => s.id === activeHoursStaff)?.firstName || ""}</div>
                {activeHoursStaff && <HoursEditor staffId={activeHoursStaff} isAdmin={canEdit} />}
                {!canEdit && <p className="text-xs text-emerald-muted mt-3">Only admins can edit working hours.</p>}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          {rows.length === 0 && <div className="text-emerald-muted">No staff yet.</div>}
          {rows.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-end gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-emerald-muted uppercase tracking-widest">Month</span>
                  <input
                    type="month"
                    value={servicesMonth}
                    onChange={(e) => { setServicesMonth(e.target.value); setServicesFrom(""); setServicesTo(""); }}
                    className="h-10 px-3 rounded-md bg-emerald-elev border border-gold-subtle text-white text-sm focus:outline-none"
                    data-testid="staff-services-month"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-emerald-muted uppercase tracking-widest">From</span>
                  <input
                    type="date"
                    value={servicesFrom}
                    min={`${servicesMonth}-01`}
                    onChange={(e) => setServicesFrom(e.target.value)}
                    className="h-10 px-3 rounded-md bg-emerald-elev border border-gold-subtle text-white text-sm focus:outline-none"
                    data-testid="staff-services-from"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-emerald-muted uppercase tracking-widest">To</span>
                  <input
                    type="date"
                    value={servicesTo}
                    min={servicesFrom || `${servicesMonth}-01`}
                    onChange={(e) => setServicesTo(e.target.value)}
                    className="h-10 px-3 rounded-md bg-emerald-elev border border-gold-subtle text-white text-sm focus:outline-none"
                    data-testid="staff-services-to"
                  />
                </div>
                {(servicesFrom || servicesTo) && (
                  <button
                    onClick={() => { setServicesFrom(""); setServicesTo(""); }}
                    className="text-xs text-emerald-muted hover:text-gold underline"
                    data-testid="staff-services-clear-dates"
                  >
                    Clear dates
                  </button>
                )}
              </div>
              <div className="grid lg:grid-cols-[160px_1fr] gap-6">
                <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-2 h-fit">
                  {rows.filter((s) => s.bookable).map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setActiveServicesStaff(s.id)}
                      data-testid={`services-staff-tab-${s.id}`}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                        activeServicesStaff === s.id
                          ? "bg-emerald-elev border border-gold text-white font-medium"
                          : "text-emerald-muted hover:text-white hover:bg-emerald-elev/60"
                      }`}
                    >
                      {s.firstName}
                    </button>
                  ))}
                </div>
                <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
                  <div className="overline mb-4">Services · {rows.find((s) => s.id === activeServicesStaff)?.firstName || ""}</div>
                  {activeServicesStaff && <StaffServicesGrid staffId={activeServicesStaff} month={servicesMonth} dateFrom={servicesFrom} dateTo={servicesTo} />}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reminders" className="mt-6">
          {rows.length === 0 && <div className="text-emerald-muted">No staff yet.</div>}
          {rows.length > 0 && (
            <div className="grid lg:grid-cols-[160px_1fr] gap-6">
              <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-2 h-fit">
                {rows.filter((s) => s.bookable).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setActiveRemindersStaff(s.id)}
                    data-testid={`reminders-staff-tab-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                      activeRemindersStaff === s.id
                        ? "bg-emerald-elev border border-gold text-white font-medium"
                        : "text-emerald-muted hover:text-white hover:bg-emerald-elev/60"
                    }`}
                  >
                    {s.firstName}
                  </button>
                ))}
              </div>
              <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6">
                <div className="overline mb-4">Reminders · {rows.find((s) => s.id === activeRemindersStaff)?.firstName || ""}</div>
                {activeRemindersStaff && <StaffRemindersGrid staffId={activeRemindersStaff} />}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}